export enum NodeTagType {
  DISCUSSION = 'discussion',
  DECISION = 'decision',
  ACTION_ITEM = 'action_item',
  QUOTE = 'quote',
  UNRESOLVED = 'unresolved',
  CONCERN = 'concern',
  KEY_INSIGHT = 'key_insight',
  TIMELINE = 'timeline',
}

export type SerializableNodeTagType =
  | 'discussion'
  | 'decision'
  | 'action_item'
  | 'quote'
  | 'unresolved'
  | 'concern'
  | 'key_insight'
  | 'timeline';

export const CATEGORY_DISPLAY_NAMES = [
  'Discussion',
  'Decision',
  'Action Item',
  'Quote',
  'Unresolved',
  'Concern',
  'Key Insight',
  'Timeline',
] as const;

export type CategoryDisplayName = (typeof CATEGORY_DISPLAY_NAMES)[number];

// Converters between enum and serializable strings
export function toSerializableTag(tag: NodeTagType): SerializableNodeTagType {
  return tag as SerializableNodeTagType;
}

export function fromSerializableTag(tag: SerializableNodeTagType): NodeTagType {
  return tag as NodeTagType;
}

export interface CategoryConfig {
  key: string;
  displayName: string;
  tagType: NodeTagType;
  colors: {
    bg: string;
    border: string;
    text: string;
  };
}

// Centralized category configuration - single source of truth
export const CATEGORY_CONFIGS: Record<NodeTagType, CategoryConfig> = {
  [NodeTagType.DISCUSSION]: {
    key: 'discussion',
    displayName: 'Discussion',
    tagType: NodeTagType.DISCUSSION,
    colors: {
      bg: '#E8F5E8',
      border: '#4CAF50',
      text: '#1B5E20',
    },
  },
  [NodeTagType.DECISION]: {
    key: 'decision',
    displayName: 'Decision',
    tagType: NodeTagType.DECISION,
    colors: {
      bg: '#FFEBEE',
      border: '#F44336',
      text: '#B71C1C',
    },
  },
  [NodeTagType.ACTION_ITEM]: {
    key: 'action_item',
    displayName: 'Action Item',
    tagType: NodeTagType.ACTION_ITEM,
    colors: {
      bg: '#E3F2FD',
      border: '#1976D2',
      text: '#0D47A1',
    },
  },
  [NodeTagType.QUOTE]: {
    key: 'quote',
    displayName: 'Quote',
    tagType: NodeTagType.QUOTE,
    colors: {
      bg: '#FFF9C4',
      border: '#F57F17',
      text: '#E65100',
    },
  },
  [NodeTagType.UNRESOLVED]: {
    key: 'unresolved',
    displayName: 'Unresolved',
    tagType: NodeTagType.UNRESOLVED,
    colors: {
      bg: '#F3E5F5',
      border: '#9C27B0',
      text: '#4A148C',
    },
  },
  [NodeTagType.CONCERN]: {
    key: 'concern',
    displayName: 'Concern',
    tagType: NodeTagType.CONCERN,
    colors: {
      bg: '#FFF3E0',
      border: '#FF9800',
      text: '#E65100',
    },
  },
  [NodeTagType.KEY_INSIGHT]: {
    key: 'key_insight',
    displayName: 'Key Insight',
    tagType: NodeTagType.KEY_INSIGHT,
    colors: {
      bg: '#E1F5FE',
      border: '#00BCD4',
      text: '#006064',
    },
  },
  [NodeTagType.TIMELINE]: {
    key: 'timeline',
    displayName: 'Timeline',
    tagType: NodeTagType.TIMELINE,
    colors: {
      bg: '#EFEBE9',
      border: '#8D6E63',
      text: '#3E2723',
    },
  },
};

// Derived exports for easy access - includes multiple name variations for better AI matching
export const CATEGORY_TO_TAG_TYPE: Record<string, NodeTagType> = Object.values(
  CATEGORY_CONFIGS
).reduce(
  (acc, config) => {
    // Add displayName variations
    acc[config.displayName.toLowerCase()] = config.tagType;
    acc[config.displayName.toLowerCase().replace(/\s+/g, '_')] = config.tagType;
    acc[config.displayName.toLowerCase().replace(/\s+/g, '')] = config.tagType;

    // Add key variations
    acc[config.key] = config.tagType;
    acc[config.key.replace(/_/g, ' ')] = config.tagType;

    // Add tagType variations
    acc[config.tagType] = config.tagType;
    acc[config.tagType.replace(/_/g, ' ')] = config.tagType;

    return acc;
  },
  {} as Record<string, NodeTagType>
);

export const TAG_COLORS: Record<
  string,
  { bg: string; border: string; text: string }
> = Object.values(CATEGORY_CONFIGS).reduce(
  (acc, config) => {
    acc[config.tagType] = config.colors;
    return acc;
  },
  {} as Record<string, { bg: string; border: string; text: string }>
);

// Helper functions
export function getCategoryConfig(tagType: NodeTagType): CategoryConfig {
  return CATEGORY_CONFIGS[tagType];
}

export function getCategoryDisplayName(tagType: NodeTagType): string {
  return CATEGORY_CONFIGS[tagType]?.displayName || tagType;
}

export function getCategoryColors(tagType: NodeTagType) {
  return (
    CATEGORY_CONFIGS[tagType]?.colors || {
      bg: '#f0f0f0',
      border: '#ccc',
      text: '#333',
    }
  );
}
